import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Endeavor Metadata API service
 */
export const EndeavorMetadataApi = {
  /**
   * Add metadata to an endeavor
   * @param {Object} data - Metadata data
   * @param {number} data.endeavorId - The endeavor ID
   * @param {number} data.metadataDefinitionId - The metadata definition ID
   * @param {string} data.value - The metadata value
   * @returns {Promise<Response>} Create response
   */
  addMetadata: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/metadata`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Update endeavor metadata
   * @param {string|number} id - Metadata ID
   * @param {Object} data - Updated metadata data
   * @returns {Promise<Response>} Update response
   */
  updateMetadata: async (id, data) => {
    // Server historically expects an update payload shaped like:
    // { id, endeavorId, fieldName, fieldValue }
    // But some front-end flows send { id, endeavorId, metadataName, value, ... }.
    // Normalize both shapes to the server's expected shape to avoid 400s.
    try {
      const payload = {
        id: id,
        endeavorId: data.endeavorId ?? data.endeavor_id ?? null,
        metadataDefinitionId: data.metadataDefinitionId ?? data.metadata_definition_id ?? undefined,
        value: data.value ?? data.fieldValue ?? undefined,
      };

      // Remove undefined keys - server may reject unexpected nulls
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      console.log('EndeavorMetadataApi: Updating metadata', id, 'payload=', payload);

      const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/metadata/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Try to surface server message for easier debugging
        let text = '';
        try { text = await response.text(); } catch (e) { /* ignore */ }
        console.error('EndeavorMetadataApi: Update failed', id, response.status, text);
        // Throw a helpful error so upstream callers can display it
        throw new Error(`EndeavorMetadataApi.updateMetadata failed: ${response.status} ${text}`);
      }

      return response;
    } catch (err) {
      console.error('EndeavorMetadataApi: Error in updateMetadata', err);
      throw err;
    }
  },

  /**
   * Delete endeavor metadata
   * @param {string|number} id - Metadata ID
   * @returns {Promise<Response>} Delete response
   */
  deleteMetadata: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/metadata/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get metadata for an endeavor
   * @param {string|number} endeavorId - Endeavor ID
   * @returns {Promise<Response>} Metadata response
   */
  getEndeavorMetadata: async (endeavorId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/metadata`, {
      method: 'GET',
    });
  }
};